/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { labelStyle } from '@configs/const.config'
import { Button, Form, FormInstance, Input, Select, Spin, Upload } from 'antd'
import TinymceInput from '@src/components/widgets/TinymceInput'
import { useInfiniteListUser, useQueryListUser } from '@src/queries/hooks'
import { EOrder, EOrderBy, ETargetNotification, TOptionSelectSearch } from '@src/configs/interface.config'
import { useEffect, useMemo, useState } from 'react'
import { TNotificationTemplate } from '@src/modules/notification-tempalte'
import { TQueryUser } from '@src/modules'
import debounce from 'lodash/debounce'
import { UploadOutlined } from '@ant-design/icons'

type TFormInput = {
  data?: TNotificationTemplate
  form: FormInstance
  onHandleUpload?: (file: File) => void
  isLoadingImportUsers?: boolean
}

const baseParam = {
  order: EOrder.DESC,
  orderBy: EOrderBy.CREATED_DATE,
  limit: 20,
  s: '',
}

function FormInput({ data, form, onHandleUpload, isLoadingImportUsers }: TFormInput) {
  const target = Form.useWatch('target', form)
  const sendsTo = useMemo(() => (data && data?.sendsTo ? data.sendsTo.map((i) => i._id) : []), [data])

  const [initOptions, setInitOptions] = useState<TOptionSelectSearch[]>([])

  const {
    data: userDataInit,
    isLoading: isLoadingUserDataInit,
    isFetching: isFetchingUserDataInit,
  } = useQueryListUser(
    { limit: 0, 'inIds[]': sendsTo },
    sendsTo?.length > 0 && data?.target === ETargetNotification.OPTION,
  )
  useEffect(() => {
    const usersInit = userDataInit?.data || []
    const theOptions: TOptionSelectSearch[] =
      usersInit?.length > 0
        ? usersInit.map((u) => ({
            value: u._id,
            label: `${u.firstName} ${u.lastName}(${u.username})`,
            key: u._id,
          }))
        : []
    setInitOptions(theOptions)
  }, [userDataInit, isLoadingUserDataInit, isFetchingUserDataInit])

  const [params, setParams] = useState<TQueryUser>({
    ...baseParam,
    'notInIds[]': sendsTo,
  })

  const {
    data: userData,
    isLoading,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteListUser(params)
  const users = useMemo(() => userData?.pages?.map((page) => page.data).flat(1) || [], [userData])

  const options = useMemo(
    () =>
      users.map((u) => ({
        label: `${u.firstName} ${u.lastName}(${u.username})`,
        value: u._id,
      })),
    [users, isLoading, isFetching, isFetchingNextPage],
  )

  const onPopupScroll = (e: any) => {
    if (e.target.scrollHeight === e.target.scrollTop + e.target.offsetHeight) {
      if (hasNextPage) {
        fetchNextPage()
      }
    }
  }

  return (
    <>
      <Form.Item
        name="title"
        label="Title"
        {...labelStyle}
        initialValue={data?.title}
        rules={[
          {
            required: true,
            message: 'Title is required!',
          },
        ]}
      >
        <Input placeholder="Please enter Title" />
      </Form.Item>
      <Form.Item
        name="target"
        label="Target"
        {...labelStyle}
        initialValue={data?.target || ETargetNotification.ALL}
        rules={[
          {
            required: true,
            message: 'Target is required!',
          },
        ]}
      >
        <Select placeholder="Select target type">
          <Select.Option value={ETargetNotification.ALL}>{ETargetNotification.ALL}</Select.Option>
          <Select.Option value={ETargetNotification.OPTION}>{ETargetNotification.OPTION}</Select.Option>
        </Select>
      </Form.Item>
      {target === ETargetNotification.OPTION && (
        <Form.Item name="sendsTo" label="Send To" {...labelStyle} initialValue={sendsTo}>
          <Select
            mode="multiple"
            maxTagCount={10}
            filterOption={false}
            onSearch={debounce(
              (value: string) =>
                setParams((prev) => ({
                  ...prev,
                  s: value,
                })),
              700,
            )}
            showSearch
            notFoundContent={isLoading ? <Spin size="small" /> : null}
            onPopupScroll={onPopupScroll}
            placeholder="Select Send To"
            options={[...initOptions, ...options]}
            loading={
              isFetching ||
              isLoading ||
              isFetchingNextPage ||
              isLoadingUserDataInit ||
              isFetchingUserDataInit ||
              isLoadingImportUsers
            }
          />
        </Form.Item>
      )}
      {data && (
        <Form.Item label="Import users" {...labelStyle}>
          <Upload
            beforeUpload={onHandleUpload}
            maxCount={1}
            fileList={[]}
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          >
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
      )}
      <Form.Item name="excerpt" label="Excerpt" {...labelStyle} initialValue={data?.excerpt}>
        <Input.TextArea placeholder="Please enter excerpt" rows={4} />
      </Form.Item>
      <Form.Item name="content" label="Content" {...labelStyle} initialValue={data?.content}>
        <TinymceInput
          {...labelStyle}
          initialValue={data?.content}
          onEditorChange={(v) => form.setFieldsValue({ content: v })}
          h={350}
        />
      </Form.Item>
    </>
  )
}

export default FormInput
